import { useEffect, useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { getDealsByDealer } from "@/Calls/callDeals";
import { Link } from "react-router-dom";
import { Car, FileText, Users, TrendingUp } from "lucide-react";
import axios from "axios";
import { API_URL } from "@/config";

const http = axios.create({ baseURL: API_URL, withCredentials: true });

interface Lead {
    cust_leadid: number;
    cust_fname: string;
    cust_lname: string;
    cust_email: string;
    cust_phoneno: string | null;
    cust_status: string | null;
    cust_lead_created_at: string | null;
}

export default function SalesDashboard() {
    const { user } = useAuth();
    const dealerId = Number(user?.dealerId ?? 0);
    const spId = user?.id;

    const [deals, setDeals] = useState<any[]>([]);
    const [leads, setLeads] = useState<Lead[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!dealerId || !spId) return;
        Promise.all([
            getDealsByDealer(dealerId, undefined).catch(() => []),
            http.get<Lead[]>(`/leads/${dealerId}`, { params: { salespersonId: spId } }).then(r => r.data).catch(() => []),
        ]).then(([d, l]) => {
            setDeals((d as any[]).filter((deal: any) => deal.salesPerson1ID === spId || deal.salesPerson2ID === spId));
            setLeads(l);
        }).finally(() => setLoading(false));
    }, [dealerId, spId]);

    const pending = deals.filter(d => d.deal_status === "PENDING").length;
    const funded = deals.filter(d => d.deal_status === "FUNDED").length;

    const stats = [
        { label: "My Deals", value: deals.length, icon: Car, color: "text-blue-600 bg-blue-50" },
        { label: "Pending", value: pending, icon: TrendingUp, color: "text-yellow-600 bg-yellow-50" },
        { label: "Funded", value: funded, icon: FileText, color: "text-green-600 bg-green-50" },
        { label: "My Leads", value: leads.length, icon: Users, color: "text-purple-600 bg-purple-50" },
    ];

    return (
        <div className="p-6 space-y-6">
            <h1 className="text-2xl font-light uppercase tracking-wide">Sales Dashboard</h1>

            {/* Stat Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {stats.map(s => (
                    <div key={s.label} className="bg-white rounded-2xl shadow p-5 flex items-center gap-4">
                        <div className={`p-3 rounded-xl ${s.color}`}>
                            <s.icon className="h-5 w-5" />
                        </div>
                        <div>
                            <p className="text-2xl font-bold">{loading ? "—" : s.value}</p>
                            <p className="text-xs text-gray-500 uppercase">{s.label}</p>
                        </div>
                    </div>
                ))}
            </div>

            {/* Recent Deals */}
            <div className="bg-white rounded-2xl shadow p-6">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="font-semibold text-lg">My Assigned Deals</h2>
                    <Link to="/sales/deals" className="text-sm text-indigo-600 hover:underline">View all</Link>
                </div>
                {loading ? (
                    <p className="text-sm text-gray-400">Loading...</p>
                ) : deals.length === 0 ? (
                    <p className="text-sm text-gray-400">No deals assigned yet.</p>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="text-xs uppercase text-gray-400 border-b">
                                <tr>
                                    <th className="text-left py-2">Vehicle</th>
                                    <th className="text-left py-2">Type</th>
                                    <th className="text-left py-2">Monthly</th>
                                    <th className="text-left py-2">Status</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50">
                                {deals.slice(0, 5).map(d => (
                                    <tr key={d.deal_id}>
                                        <td className="py-2">{[d.vYear, d.vMake, d.vModel].filter(Boolean).join(" ") || `Deal #${d.deal_id}`}</td>
                                        <td className="py-2">{d.deal_type ?? "—"}</td>
                                        <td className="py-2">{d.monthlypymts ? `$${Number(d.monthlypymts).toLocaleString()}` : "—"}</td>
                                        <td className="py-2">
                                            <span className="px-2 py-0.5 rounded-full text-xs bg-yellow-100 text-yellow-700">
                                                {d.deal_status ?? "PENDING"}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Recent Leads */}
            <div className="bg-white rounded-2xl shadow p-6">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="font-semibold text-lg">My Leads</h2>
                    <Link to="/sales/leads" className="text-sm text-indigo-600 hover:underline">View all</Link>
                </div>
                {loading ? (
                    <p className="text-sm text-gray-400">Loading...</p>
                ) : leads.length === 0 ? (
                    <p className="text-sm text-gray-400">No leads assigned yet.</p>
                ) : (
                    <div className="space-y-2">
                        {leads.slice(0, 5).map(l => (
                            <div key={l.cust_leadid} className="flex items-center justify-between rounded-xl border px-4 py-3">
                                <div>
                                    <p className="font-medium">{l.cust_fname} {l.cust_lname}</p>
                                    <p className="text-xs text-gray-400">{l.cust_email}</p>
                                </div>
                                <span className="text-xs px-2 py-0.5 bg-indigo-100 text-indigo-700 rounded-full">
                                    {l.cust_status ?? "NEW"}
                                </span>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
